import {
  DragState,
  DrawingState,
  LineEndType,
  Point,
  ShapeType,
  SnapPoint,
  ToolMode,
} from 'interfaces/interface-studio-canvas';
import { create } from 'zustand';

interface DrawingStore {
  selectedShapeId: string | null;
  drawingState: DrawingState;
  toolMode: ToolMode;
  currentTool: ShapeType;
  lineColor: string;
  fillColor: string;
  lineWidth: number;
  startEnd: LineEndType;
  endEnd: LineEndType;
  showFill: boolean;
  borderRadius: number;
  snapPoints: SnapPoint[];
  isSnapping: boolean;
  snapDistance: number;

  // Enhanced shape controls
  starSpikes: number;
  starInnerRadius: number;
  pieStartAngle: number;
  pieEndAngle: number;

  dragState: DragState;
  currentMousePos: Point | null;

  // Setters
  setSelectedShapeId: (id: string | null) => void;
  setDrawingState: (state: DrawingState) => void;
  setToolMode: (mode: ToolMode) => void;
  setCurrentTool: (tool: ShapeType) => void;
  setLineColor: (color: string) => void;
  setFillColor: (color: string) => void;
  setLineWidth: (width: number) => void;
  setStartEnd: (end: LineEndType) => void;
  setEndEnd: (end: LineEndType) => void;
  setShowFill: (show: boolean) => void;
  setBorderRadius: (radius: number) => void;
  setSnapPoints: (points: SnapPoint[]) => void;
  setIsSnapping: (snapping: boolean) => void;

  setStarSpikes: (spikes: number) => void;
  setStarInnerRadius: (radius: number) => void;
  setPieStartAngle: (angle: number) => void;
  setPieEndAngle: (angle: number) => void;

  setDragState: (state: DragState) => void;

  isHoveringEndpoint: boolean;
  setIsHoveringEndpoint: (hovering: boolean) => void;
  setCurrentMousePos: (currentMousePosition: Point | null) => void;
}

export const useDrawingStore = create<DrawingStore>()((set) => ({
  selectedShapeId: null,
  drawingState: {
    isDrawing: false,
    currentShape: [],
    shapeType: 'line',
  },
  //   toolMode: 'draw',
  toolMode: 'select',
  currentTool: 'line',
  lineColor: '#000000',
  fillColor: '#ffffff',
  lineWidth: 2,
  startEnd: 'none',
  endEnd: 'none',
  showFill: false,
  borderRadius: 0,
  snapPoints: [],
  isSnapping: true,
  snapDistance: 15,

  // Enhanced shapes
  starSpikes: 5,
  starInnerRadius: 0.4,
  pieStartAngle: 0,
  pieEndAngle: 90,

  dragState: {
    isDragging: false,
    shapeId: null,
    pointIndex: null,
    offset: { x: 0, y: 0 },
  },

  isHoveringEndpoint: false,
  currentMousePos: null,

  // Setters
  setSelectedShapeId: (id) => set({ selectedShapeId: id }),
  setDrawingState: (state) => set({ drawingState: state }),
  setToolMode: (mode) => set({ toolMode: mode }),
  setCurrentTool: (tool) => set({ currentTool: tool }),
  setLineColor: (color) => set({ lineColor: color }),
  setFillColor: (color) => set({ fillColor: color }),
  setLineWidth: (width) => set({ lineWidth: width }),
  setStartEnd: (end) => set({ startEnd: end }),
  setEndEnd: (end) => set({ endEnd: end }),
  setShowFill: (show) => set({ showFill: show }),
  setBorderRadius: (radius) => set({ borderRadius: radius }),
  setSnapPoints: (points) => set({ snapPoints: points }),
  setIsSnapping: (snapping) => set({ isSnapping: snapping }),

  setStarSpikes: (spikes) => set({ starSpikes: spikes }),
  setStarInnerRadius: (radius) => set({ starInnerRadius: radius }),
  setPieStartAngle: (angle) => set({ pieStartAngle: angle }),
  setPieEndAngle: (angle) => set({ pieEndAngle: angle }),

  setDragState: (state) => {
    set({ dragState: state });
  },
  setIsHoveringEndpoint: (state) => set({ isHoveringEndpoint: state }),
  setCurrentMousePos: (state) => set({ currentMousePos: state }),
}));
